// ============================================================
// useMarketSocket — eine WS-Verbindung zu /ws/market.
// Reconnect mit Backoff, re-abonniert Subscriptions nach Reconnect.
// Hochfrequente Klines werden per Callback geliefert (kein State-Spam).
// ============================================================
import { useEffect, useRef, useState, useCallback } from 'react';
import { MarketMsg, TickerMsg, KlineMsg } from './types';

type Status = 'connecting' | 'open' | 'closed';

interface Handlers {
  onTicker?: (t: TickerMsg) => void;
  onKline?: (k: KlineMsg) => void;
}

function wsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/market`;
}

export function useMarketSocket(handlers: Handlers) {
  const [status, setStatus] = useState<Status>('connecting');
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1000);
  const closedRef = useRef(false);

  // Gewünschter Sub-Zustand, damit wir nach Reconnect alles neu senden.
  const watchlistRef = useRef(false);
  const activeRef = useRef<{ symbol: string; interval: string } | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const rawSend = (obj: any) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  };

  const connect = useCallback(() => {
    if (closedRef.current) return;
    setStatus('connecting');
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      backoffRef.current = 1000;
      setStatus('open');
      // Subscriptions wiederherstellen.
      if (watchlistRef.current) rawSend({ op: 'sub', channel: 'watchlist' });
      if (activeRef.current) rawSend({ op: 'sub', channel: 'active', ...activeRef.current });
    };

    ws.onmessage = (ev) => {
      let msg: MarketMsg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === 'ticker') handlersRef.current.onTicker?.(msg);
      else if (msg.type === 'kline') handlersRef.current.onKline?.(msg);
    };

    ws.onclose = () => {
      setStatus('closed');
      wsRef.current = null;
      if (closedRef.current) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(delay * 2, 15000);
      setTimeout(connect, delay);
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  }, []);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  const subscribeWatchlist = useCallback(() => {
    watchlistRef.current = true;
    rawSend({ op: 'sub', channel: 'watchlist' });
  }, []);

  // Aktives Symbol/TF wechseln: altes abmelden, neues anmelden.
  const setActive = useCallback((symbol: string, interval: string) => {
    const prev = activeRef.current;
    if (prev && (prev.symbol !== symbol || prev.interval !== interval)) {
      rawSend({ op: 'unsub', channel: 'active', ...prev });
    }
    activeRef.current = { symbol, interval };
    rawSend({ op: 'sub', channel: 'active', symbol, interval });
  }, []);

  return { status, subscribeWatchlist, setActive };
}
